import { ChevronDown, Link2 } from 'lucide-react';
import { useState } from 'react';

import type { ParsedMessage, Turn } from '@/features/votes/chat-message';
import type {
  DebugPayloadSkeleton,
  PayloadSection,
  SystemPrompt,
} from '@/features/votes/message-votes';
import { cn } from '@/lib/utils';

import MessageHeader from './MessageHeader';
import MessageRenderer from './MessageRenderer';
import PayloadStepsTable from './PayloadStepsTable';
import SystemPromptBlock from './SystemPromptBlock';

function CompactionSummaryCard({ summary }: { summary: string }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 dark:bg-amber-900/20">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-xs font-semibold text-amber-800 dark:text-amber-200"
      >
        Across-turns compaction — history before this message was summarized
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>
      {open && (
        <div className="mt-2 whitespace-pre-wrap text-xs text-amber-900/90 dark:text-amber-100/90">
          {summary}
        </div>
      )}
    </div>
  );
}

type Segment =
  | { kind: 'user'; message: ParsedMessage }
  | { kind: 'agent'; messages: ParsedMessage[] };

// Split a turn's messages into user bubbles and contiguous agent runs
// (assistant + tool), so each agent run shares one continuous left border.
function toSegments(messages: ParsedMessage[]): Segment[] {
  const segments: Segment[] = [];
  for (const message of messages) {
    if (message.role === 'user') {
      segments.push({ kind: 'user', message });
    } else {
      const last = segments[segments.length - 1];
      if (last && last.kind === 'agent') last.messages.push(message);
      else segments.push({ kind: 'agent', messages: [message] });
    }
  }
  return segments;
}

interface TurnGroupProps {
  turn: Turn;
  // Vote pages only: enables the per-turn permalink into the downvote view.
  voteId?: number;
  // Flat index of the message this view is about, badged with `highlightLabel`.
  highlightedFlatIndex?: number;
  highlightedMessageRef?: React.Ref<HTMLDivElement>;
  highlightLabel?: string;
  compactionSummaries?: Record<string, string>;
  systemPrompts?: Record<string, SystemPrompt>;
  // Message-vote pages only. The entity-vote page renders generation chats that have no payloads.
  payloads?: DebugPayloadSkeleton[];
  previousTurnSections?: PayloadSection[];
  onCopyPayload?: (messageId: string, stepIndex: number) => Promise<void>;
}

export default function TurnGroup({
  turn,
  voteId,
  highlightedFlatIndex = -1,
  highlightedMessageRef,
  highlightLabel = 'Voted',
  compactionSummaries,
  systemPrompts = {},
  payloads = [],
  previousTurnSections = [],
  onCopyPayload,
}: TurnGroupProps) {
  const [copied, setCopied] = useState(false);
  const copyLink = () => {
    navigator.clipboard.writeText(
      `${window.location.origin}/downvote/${voteId}#${turn.turnNumber}`
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 900);
  };
  const segments = toSegments(turn.messages);
  // The steps table describes the whole turn, so it renders once against the user message that
  // opened it — between that message and its system prompts, matching the request's own order.
  const firstUserSegment = segments.findIndex(
    (segment) => segment.kind === 'user'
  );
  return (
    <div id={`turn-${turn.turnNumber}`} className="scroll-mt-24">
      <div className="mb-1 flex items-center gap-2">
        <span className="text-[11px] font-semibold text-muted-foreground">
          Turn {turn.turnNumber}
        </span>
        {voteId !== undefined && (
          <button
            onClick={copyLink}
            title="Copy link to this turn"
            className="flex items-center gap-1 text-[11px] text-foreground transition-colors hover:text-muted-foreground/60"
          >
            {copied ? 'Link copied' : <Link2 className="h-3 w-3" />}
          </button>
        )}
      </div>
      <div className="space-y-3">
        {segments.map((segment, i) => {
          if (segment.kind === 'user') {
            const compactionSummary = segment.message.id
              ? compactionSummaries?.[segment.message.id]
              : undefined;
            const isTrigger = !!compactionSummary;
            const systemPrompt = segment.message.id
              ? systemPrompts[segment.message.id]
              : undefined;
            return (
              <div key={segment.message.index}>
                {compactionSummary && (
                  <CompactionSummaryCard summary={compactionSummary} />
                )}
                <MessageRenderer
                  ref={
                    segment.message.index === highlightedFlatIndex
                      ? highlightedMessageRef
                      : undefined
                  }
                  message={segment.message}
                  highlightLabel={
                    segment.message.index === highlightedFlatIndex
                      ? highlightLabel
                      : undefined
                  }
                  isCompactionTrigger={isTrigger}
                  variant="bubble"
                />
                {i === firstUserSegment &&
                  payloads.length > 0 &&
                  onCopyPayload && (
                    <div className="mt-2">
                      <PayloadStepsTable
                        payloads={payloads}
                        previousTurnSections={previousTurnSections}
                        onCopyPayload={onCopyPayload}
                      />
                    </div>
                  )}
                {systemPrompt && (
                  <div className="mt-2">
                    <SystemPromptBlock prompt={systemPrompt} />
                  </div>
                )}
              </div>
            );
          }
          const model = segment.messages.find(
            (m) => m.role === 'assistant' && m.agent
          )?.agent;
          const messageId = segment.messages.find(
            (m) => m.role === 'assistant'
          )?.id;
          return (
            <div
              key={`agent-${i}`}
              className="space-y-3 border-l-2 border-l-emerald-400/70 py-1 pl-4"
            >
              <MessageHeader role="assistant" agent={model} id={messageId} />
              {segment.messages.map((message) => (
                <MessageRenderer
                  key={message.index}
                  ref={
                    message.index === highlightedFlatIndex
                      ? highlightedMessageRef
                      : undefined
                  }
                  message={message}
                  highlightLabel={
                    message.index === highlightedFlatIndex
                      ? highlightLabel
                      : undefined
                  }
                  variant="plain"
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
